import { ProductJourney } from "@shared/analytics/productEvents";
import {
    API_URLS,
    type RetailWalletChallengeResponse,
    type YaasPositionSplit,
    type YaasWalletResponse
} from "@solomon-apps/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { observeSettingsVerification } from "@/services/analytics/settings";

import { yaasWalletQueryKey } from "./queryKeys";
import { useWalletVerification, type VerificationProof } from "./useWalletVerification";
import { requestWalletActionChallenge } from "./verificationRequests";

export interface PositionSplitInput {
    savedDestinationUuid: string;
    percentageBps: number;
}

export interface SetPositionSplitsResponse {
    positionId: number;
    splits: YaasPositionSplit[];
}

export type SetPositionSplitsResult =
    | { success: true; data: SetPositionSplitsResponse }
    | { success: false; error: string };

export interface UsePositionSplitsResult {
    setPositionSplits: (input: {
        walletAddress: string;
        analytics?: ProductJourney;
        positionId: number;
        splits: PositionSplitInput[];
    }) => Promise<SetPositionSplitsResult>;
    isLoading: boolean;
    error: string | null;
}

export function positionSplitsUrl(apiBase: string, walletAddress: string, positionId: number): string {
    return `${apiBase}/api/yaas/wallet/${walletAddress}/positions/${positionId}/splits`;
}

export function positionSplitsChallengeUrl(apiBase: string, walletAddress: string, positionId: number): string {
    return `${positionSplitsUrl(apiBase, walletAddress, positionId)}/challenge`;
}

function splitBody(splits: PositionSplitInput[]): Record<string, unknown> {
    return { splits };
}

async function requestPositionSplitsChallenge(input: {
    walletAddress: string;
    analytics?: ProductJourney;
    positionId: number;
    splits: PositionSplitInput[];
}): Promise<RetailWalletChallengeResponse> {
    return requestWalletActionChallenge(
        positionSplitsChallengeUrl(API_URLS.YAAS, input.walletAddress, input.positionId),
        splitBody(input.splits),
        "Failed to request position split challenge"
    );
}

async function submitPositionSplits(input: {
    walletAddress: string;
    analytics?: ProductJourney;
    positionId: number;
    splits: PositionSplitInput[];
    nonce: string;
    proof: VerificationProof;
}): Promise<SetPositionSplitsResponse> {
    const response = await fetch(positionSplitsUrl(API_URLS.YAAS, input.walletAddress, input.positionId), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            splits: input.splits,
            nonce: input.nonce,
            proof: input.proof
        })
    });
    if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || body.message || "Failed to update position splits");
    }
    return response.json() as Promise<SetPositionSplitsResponse>;
}

export function usePositionSplits(): UsePositionSplitsResult {
    const walletVerification = useWalletVerification();
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const setPositionSplits = async (input: {
        walletAddress: string;
        analytics?: ProductJourney;
        positionId: number;
        splits: PositionSplitInput[];
    }): Promise<SetPositionSplitsResult> => {
        const analytics = input.analytics ?? new ProductJourney("rewards_routing", "position_row", input.walletAddress);
        const attempt = analytics.attempt({
            destination_count: input.splits.length,
            allocation_total_bps: input.splits.reduce((total, split) => total + split.percentageBps, 0)
        });
        attempt.send("challenge_started");
        setIsLoading(true);
        setError(null);
        try {
            const challenge = await requestPositionSplitsChallenge(input);
            attempt.challengeCompleted();
            const proof = await walletVerification.verify({
                purpose: "action",
                analytics: observeSettingsVerification(attempt),
                walletAddress: input.walletAddress,
                message: challenge.message,
                nonce: challenge.nonce,
                expiresAt: challenge.expiresAt,
                microTransfer: challenge.microTransfer
            });
            attempt.submit(proof.method);
            const data = await submitPositionSplits({
                ...input,
                nonce: challenge.nonce,
                proof
            });
            attempt.complete();
            queryClient.setQueryData<YaasWalletResponse>(yaasWalletQueryKey(input.walletAddress), (prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    programPositions: prev.programPositions.map((position) =>
                        // Normalize both sides: GET serializes `id` as a
                        // number, the PUT echoes the un-coerced route-param
                        // `positionId` as a string — a strict `===` would
                        // silently no-op and leave the UI showing old splits.
                        String(position.id) === String(data.positionId)
                            ? { ...position, splits: data.splits }
                            : position
                    )
                };
            });
            return { success: true, data };
        } catch (err: unknown) {
            attempt.fail(err);
            const message = err instanceof Error ? err.message : "Failed to update position splits";
            setError(message);
            return { success: false, error: message };
        } finally {
            setIsLoading(false);
        }
    };

    return { setPositionSplits, isLoading, error };
}
